// Full-screen sign-in. Shows the shared EmailAuthForm (password + magic-link
// + Google) plus an escape hatch that keeps the user in local-only mode. Used
// when a returning user explicitly re-arms the sign-in flow from the user menu.
//
// A "How HanZi World works" affordance opens the same DemoStep tutorial the
// pre-auth welcome flow uses, so users arriving here without having seen the
// welcome can still preview the loop before signing in.

import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { C } from '@/styles/tokens';
import { EmailAuthForm } from './EmailAuthForm';
import { DemoStep } from '@/features/onboarding/DemoStep';

interface SignInScreenProps {
  onSkip: () => void;
}

export function SignInScreen({ onSkip }: SignInScreenProps) {
  const [showDemo, setShowDemo] = useState(false);

  if (showDemo) {
    return (
      <section
        className="h-[100dvh] flex flex-col safe-top safe-bottom px-6 pt-4 pb-6"
        style={{ background: C.cream }}
      >
        <header className="flex items-center mb-6 min-h-[28px]">
          <button
            type="button"
            onClick={() => setShowDemo(false)}
            className="-ml-2 p-2 rounded-full inline-flex items-center gap-1 ff-body text-[12px]"
            style={{ color: C.inkLight }}
            aria-label="Back to sign in"
          >
            <ChevronLeft size={20} aria-hidden />
            <span>Back to sign in</span>
          </button>
        </header>
        <DemoStep onContinue={() => setShowDemo(false)} />
      </section>
    );
  }

  return (
    <section
      className="h-[100dvh] flex flex-col items-center justify-between px-6 pt-12 pb-8 safe-top safe-bottom"
      style={{ background: C.cream }}
    >
      <div className="flex-1 flex flex-col items-center justify-center w-full max-w-sm">
        <div className="flex flex-col items-center text-center mb-6">
          <div
            aria-hidden
            className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
            style={{
              background: C.vermillion,
              boxShadow: '0 6px 18px rgba(233,75,60,0.32)',
            }}
          >
            <span className="ff-zh-serif text-white text-3xl font-bold leading-none">字</span>
          </div>
          <h1 className="ff-display italic text-4xl mb-3" style={{ color: C.ink }}>
            HanZi World
          </h1>
          <p className="ff-body text-[13px] leading-relaxed max-w-[300px]" style={{ color: C.inkLight }}>
            Discover HanZi World!
          </p>
        </div>

        <EmailAuthForm layoutSize="fullscreen" />
      </div>

      <div className="w-full max-w-sm flex flex-col items-center gap-1.5">
        <button
          type="button"
          onClick={() => setShowDemo(true)}
          className="py-2 px-5 rounded-full ff-body text-[12px] font-semibold text-white inline-flex items-center justify-center gap-1.5"
          style={{ background: C.vermillion }}
        >
          How HanZi World works
          <ChevronRight size={14} aria-hidden />
        </button>
        <button
          type="button"
          onClick={onSkip}
          className="ff-body text-[10px] uppercase tracking-[0.2em] font-semibold py-1.5 px-3"
          style={{ color: C.inkLight }}
        >
          Continue without account →
        </button>
      </div>
    </section>
  );
}
